import authApi from '../../api/auth';
import { ModalActivationContext } from '../../contexts';
import { isTablet } from '../../services/mobile';
import style from './Profile.module.scss';
import { DownloadAllFilesBlock } from 'components/DownloadAllFilesBlock';
import { Button } from 'components/InteractiveUIControls/Button/Button';
import { Svgicon } from 'components/Svgicon/Svgicon';
import { useRelativePathsToBasePath } from 'hooks';
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { type AppDispatch } from 'store';
import { type OrganizationsStoreState } from 'store/slices';
import { getItemsFromStorage } from 'types';

export const Profile = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { toggleSettingsModal } = useContext(ModalActivationContext);
  const { valid: isAccountsPath } =
  useRelativePathsToBasePath('/firm/capital-accounts/*');
  const componentRef = useRef(
    null,
  ) as unknown as React.MutableRefObject<HTMLDivElement>;

  const {
    displayedOrganizationWithPermissions } = useSelector<{
    organizations: OrganizationsStoreState,
  }>((state) => state.organizations) as OrganizationsStoreState;

  const {
    showSupportModal,
    toggleSupportModal,
  } = useContext(ModalActivationContext);

  const [
    showSelection,
    setShowSelection,
  ] = useState(false);

  const { loginAs } = getItemsFromStorage([
    'loginAs',
  ], sessionStorage);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      showSelection &&
      componentRef.current &&
      !componentRef.current.contains(event.target as Node)
    ) {
      setShowSelection(false);
    }
  }, [
    showSelection,
  ]);

  const handleSettingsClick = useCallback(() => {
    toggleSettingsModal(true);
    setShowSelection(false);
  }, [
    navigate,
    toggleSettingsModal,
  ]);

  const handleSupportClick = useCallback(() => {
    toggleSupportModal(!showSupportModal);
    setShowSelection(false);
  }, [
    showSupportModal,
    toggleSupportModal,
  ]);

  const handleProfileButtonClick = useCallback(
    (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event?.stopPropagation();
      setShowSelection(!showSelection);
    }, [
      showSelection,
    ]);

  const logout = () => {
    toggleSupportModal(false);
    dispatch(authApi.logout(navigate));
  };

  useEffect(() => {
    // Add a click event listener to the document
    document.addEventListener('click', handleClickOutside);

    return () => {
      // Remove the event listener when the component unmounts
      document.removeEventListener('click', handleClickOutside);
    };
  }, [
    handleClickOutside,
  ]);

  const menu = () => <div className={style.activeBlock} ref={componentRef}>
    {!loginAs && <Button
      className={style.blockOption}
      data-test={'click-settings-button'}
      onClick={handleSettingsClick}
      tooltipWrapperClass='!justify-start'
      type='transparent'
    >
      <Svgicon className='size-[1.25rem] ms:size-[0.875rem]' color='black' id='user-profile-03' />
      <span className='text-font-1 font-medium ms:text-font-3'>
        Account settings
      </span>
    </Button>}
    {isTablet() ? <div className='h-[0.0625rem] w-full bg-black-200' /> : null}
    {isAccountsPath ? null : <Button
      className={style.blockOption}
      data-test={'click-download-button'}
      disabled={!displayedOrganizationWithPermissions}
      onClick={() => setShowSelection(false)}
      tooltipWrapperClass='!justify-start'
      type='transparent'
    >
      <Svgicon className='size-[1.25rem] ms:size-[1rem]' id='download-cloud' />
      <DownloadAllFilesBlock />
    </Button>}
    {isTablet() && !isAccountsPath ? <div className='h-[0.0625rem] w-full bg-black-200' /> : null}
    <Button
      className={style.blockOption}
      data-test={'click-support-button'}
      onClick={handleSupportClick}
      tooltipWrapperClass='!justify-start'
      type='transparent'
    >
      <Svgicon className='size-[1.25rem] ms:size-[1rem]' id='support-1f' />
      <span className='text-font-1 font-medium ms:text-font-3'>
        Get support
      </span>
    </Button>
    {isTablet() ? <div className='h-[0.0625rem] w-full bg-black-200' /> : null}
    <Button
      className={style.blockOption}
      data-test={'click-logout-button'}
      onClick={logout}
      tooltipWrapperClass='!justify-start'
      type='transparent'
    >
      <Svgicon className='size-[1.25rem] ms:size-[1rem]' id='logout-3f' />
      <span className='text-font-1 font-medium ms:text-font-3'>
        Log out
      </span>
    </Button>
  </div>;

  const iconId = () => {
    if (!isTablet()) {
      return 'user-profile-03';
    }

    return showSelection ? 'menu-close' : 'sidebar-menu-2f';
  };

  return (
    <div className='relative flex items-center gap-0.5'>
      <Button
        className='relative !size-10'
        data-test='click-profile-button'
        icon='only'
        iconClassName='size-[2rem] ms:!size-[1rem]'
        iconColor={'black'}
        iconid={iconId()}
        onClick={(event) => handleProfileButtonClick(event)}
        squared
        tooltipWrapperClass='z-[999]'
        type={isTablet() ? 'transparent' : 'outline-grey'}
      />
      {showSelection && <div className={isTablet() ? style.backdrop : style.backdropDesktop} />}
      {showSelection && menu()}
    </div>
  );
};
